/**
 * Admin Question Bank Edit Page
 * Create or edit a question bank with questions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { questionBanksApi, questionsApi } from '../api';
import { QuestionBankWithQuestions, QuestionWithOptions, CreateQuestionInput, CreateAnswerOptionInput, UpdateQuestionInput } from '../../types/models';
import { QuestionEditor } from '../components/QuestionEditor.js';
import { PDFPreviewModal, type ExtractedQuestion } from '../components/PDFPreviewModal';
import { CSVPreviewModal } from '../components/CSVPreviewModal';
import { LoadingModal } from '../components/LoadingModal';
import { AlertModal } from '../components/AlertModal';

// CSV Upload Modal Component
interface CSVUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (questionsFile: File | null, optionsFile: File | null) => void;
  disabled: boolean;
  isUploading: boolean;
}

const CSVUploadModal: React.FC<CSVUploadModalProps> = ({ isOpen, onClose, onUpload, disabled, isUploading }) => {
  const [questionsFile, setQuestionsFile] = useState<File | null>(null);
  const [optionsFile, setOptionsFile] = useState<File | null>(null);
  const questionsInputRef = React.useRef<HTMLInputElement>(null);
  const optionsInputRef = React.useRef<HTMLInputElement>(null);

  const handleQuestionsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setQuestionsFile(file);
  };

  const handleOptionsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setOptionsFile(file);
  };

  const handleUpload = () => {
    if (questionsFile && optionsFile) {
      onUpload(questionsFile, optionsFile);
      // Reset state
      setQuestionsFile(null);
      setOptionsFile(null);
      if (questionsInputRef.current) questionsInputRef.current.value = '';
      if (optionsInputRef.current) optionsInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setQuestionsFile(null);
      setOptionsFile(null);
      if (questionsInputRef.current) questionsInputRef.current.value = '';
      if (optionsInputRef.current) optionsInputRef.current.value = '';
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      style={{ zIndex: 1000 }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          opacity: isUploading ? 0.5 : 1,
        }}
      >
        <div className="modal-header">
          <h2>Import Questions from CSV</h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isUploading}
            style={{ opacity: isUploading ? 0.5 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            Please select both CSV files to import questions. One file should contain the questions, and the other should contain the answer options.
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="csv-questions-file" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Questions CSV File
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <label 
                className="btn btn-secondary" 
                style={{ 
                  cursor: disabled || isUploading ? 'not-allowed' : 'pointer', 
                  margin: 0,
                  opacity: (disabled || isUploading) ? 0.6 : 1
                }}
              >
                <span>Choose File</span>
                <input
                  id="csv-questions-file"
                  ref={questionsInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleQuestionsFileChange}
                  disabled={disabled || isUploading}
                  style={{ display: 'none' }}
                />
              </label>
              {questionsFile && (
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{questionsFile.name}</span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="csv-options-file" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Options CSV File
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <label 
                className="btn btn-secondary" 
                style={{ 
                  cursor: disabled || isUploading ? 'not-allowed' : 'pointer', 
                  margin: 0,
                  opacity: (disabled || isUploading) ? 0.6 : 1
                }}
              >
                <span>Choose File</span>
                <input
                  id="csv-options-file"
                  ref={optionsInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleOptionsFileChange}
                  disabled={disabled || isUploading}
                  style={{ display: 'none' }}
                />
              </label>
              {optionsFile && (
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{optionsFile.name}</span>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button 
            onClick={handleClose} 
            className="btn btn-secondary"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="btn btn-primary"
            disabled={!questionsFile || !optionsFile || isUploading}
          >
            {isUploading ? 'Analyzing...' : 'Import CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminQuestionBankEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [questionBank, setQuestionBank] = useState<Partial<QuestionBankWithQuestions>>({
    name: '',
    description: '',
  });
  const [questions, setQuestions] = useState<QuestionWithOptions[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithOptions | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState<ExtractedQuestion[]>([]);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  // CSV import state
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [showCsvPreviewModal, setShowCsvPreviewModal] = useState(false);
  const [csvPreviewQuestions, setCsvPreviewQuestions] = useState<any[]>([]);
  const [csvPreviewErrors, setCsvPreviewErrors] = useState<string[]>([]);
  const [csvPreviewWarnings, setCsvPreviewWarnings] = useState<string[]>([]);
  const [csvParsingMethod, setCsvParsingMethod] = useState<'structured' | 'ai' | undefined>(undefined);

  const loadQuestionBank = async () => {
    if (!id || isNew) return;
    
    try {
      setLoading(true);
      const data = await questionBanksApi.getByIdWithQuestions(id);
      setQuestionBank(data);
      setQuestions(data.questions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load question bank');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentIsNew = !id || id === 'new';
    
    if (!currentIsNew && id) {
      loadQuestionBank();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const currentIsNew = !id || id === 'new';
      
      if (currentIsNew) {
        const questionBankData: any = {
          name: questionBank.name,
          description: questionBank.description || null,
          created_by: '00000000-0000-0000-0000-000000000000', // Default user for POC
        };
        
        const created = await questionBanksApi.create(questionBankData);
        navigate(`/admin/question-banks/${created.id}`);
      } else {
        const questionBankData: any = {
          name: questionBank.name,
          description: questionBank.description || null,
        };
        
        await questionBanksApi.update(id!, questionBankData);
        setAlert({ message: 'Question bank saved successfully!', type: 'success' });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save question bank');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }
    try {
      await questionsApi.delete(questionId);
      setQuestions(questions.filter((q) => q.id !== questionId));
      setAlert({ message: 'Question deleted successfully', type: 'success' });
    } catch (err: any) {
      setAlert({ message: err.message || 'Failed to delete question', type: 'error' });
    }
  };

  const handleSaveQuestion = async (
    question: CreateQuestionInput,
    options: CreateAnswerOptionInput[]
  ) => {
    if (!id || isNew) {
      setAlert({ message: 'Please save the question bank first before adding questions.', type: 'warning' });
      return;
    }

    try {
      // Calculate sequence order (next available)
      const maxSequence = questions.length > 0 
        ? Math.max(...questions.map((q) => q.sequence_order))
        : 0;
      
      const questionData: CreateQuestionInput = {
        ...question,
        question_bank_id: id,
        sequence_order: maxSequence + 1,
      };

      const created = await questionsApi.create(questionData);
      
      // Create answer options
      if (options.length > 0) {
        // Remove question_id from options as it's set by the endpoint from the URL parameter
        const optionsWithoutQuestionId = options.map(({ question_id, ...opt }) => opt);
        await questionsApi.createOptions(created.id, optionsWithoutQuestionId);
      }

      // Reload questions
      const updated = await questionBanksApi.getByIdWithQuestions(id);
      setQuestions(updated.questions || []);
      
      setShowQuestionForm(false);
      setAlert({ message: 'Question saved successfully', type: 'success' });
    } catch (err: any) {
      setAlert({ message: err.message || 'Failed to save question', type: 'error' });
    }
  };

  const handleUpdateQuestion = async (
    question: CreateQuestionInput,
    options: CreateAnswerOptionInput[]
  ) => {
    if (!id || isNew || !editingQuestion) {
      return;
    }

    try {
      const questionId = editingQuestion.id;

      // Update question (exclude assessment_id and question_bank_id from update)
      const updateData: UpdateQuestionInput = {
        sequence_order: question.sequence_order,
        question_type: question.question_type,
        prompt_text: question.prompt_text,
        points_possible: question.points_possible,
        explanation: question.explanation,
        category: question.category,
        allow_partial_credit: question.allow_partial_credit,
      };
      await questionsApi.update(questionId, updateData);

      // Delete all existing options and recreate them
      // In a production app, you'd want to do a proper diff
      for (const option of editingQuestion.answer_options) {
        await questionsApi.deleteOption(questionId, option.id);
      }

      // Create new options
      if (options.length > 0) {
        // Remove question_id from options as it's set by the endpoint from the URL parameter
        const optionsWithoutQuestionId = options.map(({ question_id, ...opt }) => opt);
        await questionsApi.createOptions(questionId, optionsWithoutQuestionId);
      }

      // Reload questions
      const updated = await questionBanksApi.getByIdWithQuestions(id);
      setQuestions(updated.questions || []);
      
      setEditingQuestion(null);
      setShowQuestionForm(false);
      setAlert({ message: 'Question updated successfully', type: 'success' });
    } catch (err: any) {
      setAlert({ message: err.message || 'Failed to update question', type: 'error' });
    }
  };

  const handleEditQuestion = (question: QuestionWithOptions) => {
    setEditingQuestion(question);
    setShowQuestionForm(true);
    // Scroll to question form
    const anchor = document.getElementById('question-editor-anchor');
    if (anchor) {
      // slight pause so question section is rendered and scrolled to the correct place
      setTimeout(() => {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleCancelQuestion = () => {
    setEditingQuestion(null);
    setShowQuestionForm(false);
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || isNew) {
      setError('Please save the question bank first before uploading a PDF');
      return;
    }

    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('PDF file size must be less than 50MB');
      return;
    }

    try {
      setUploadingPdf(true);
      setError(null);
      
      // Analyze PDF and show preview
      const result = await questionBanksApi.analyzePdf(id, file);
      
      // Store file and questions for preview (even if empty, show errors)
      setPdfFile(file);
      setPreviewQuestions(result.questions);
      setPreviewErrors(result.errors || []);
      setShowPreviewModal(true);
      
      // Show alert if no questions extracted (errors will be shown in modal)
      if (result.questions.length === 0 && (!result.errors || result.errors.length === 0)) {
        setAlert({ message: 'No questions could be extracted from the PDF. Try adding context in the preview modal to help the AI better understand your questions.', type: 'warning' });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to analyze PDF');
    } finally {
      setUploadingPdf(false);
      // Reset file input
      input.value = '';
    }
  };

  const handleSavePreviewQuestions = async (questions: any[]) => {
    if (!id || isNew || savingQuestions) return; // Prevent double submission

    try {
      setSavingQuestions(true);
      setError(null);

      // Create questions from preview
      const result = await questionBanksApi.uploadPdf(id, null, questions);
      
      // Show success message
      const message = `Successfully created ${result.questions_created} question(s)!` +
        (result.errors && result.errors.length > 0
          ? `\n\nNote: ${result.errors.length} question(s) failed to create`
          : '');
      setAlert({ message, type: 'success' });

      // Close modal and reload questions
      setShowPreviewModal(false);
      setPreviewQuestions([]);
      setPdfFile(null);
      await loadQuestionBank();
    } catch (err: any) {
      setError(err.message || 'Failed to create questions');
    } finally {
      setSavingQuestions(false);
    }
  };

  const handleReanalyzePdf = async (context: string) => {
    if (!id || isNew || !pdfFile) return;

    try {
      setIsReanalyzing(true);
      setError(null);

      // Re-analyze with context
      const result = await questionBanksApi.analyzePdf(id, pdfFile, context);
      
      // Update preview with new questions and errors
      setPreviewQuestions(result.questions);
      setPreviewErrors(result.errors || []);
      
      if (result.questions.length === 0 && (!result.errors || result.errors.length === 0)) {
        setAlert({ message: 'No questions could be extracted. Try adjusting your context instructions.', type: 'warning' });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to re-analyze PDF');
    } finally {
      setIsReanalyzing(false);
    }
  };

  const handleCancelPreview = () => {
    setShowPreviewModal(false);
    setPreviewQuestions([]);
    setPreviewErrors([]);
    setPdfFile(null);
  };

  const handleCsvUpload = async (
    questionsFile: File | null,
    optionsFile: File | null
  ) => {
    if (!id || isNew) {
      setError('Please save the question bank first before uploading CSV files');
      setShowCsvUploadModal(false);
      return;
    }

    if (!questionsFile || !optionsFile) {
      setError('Please select both questions CSV and options CSV files');
      return;
    }

    // Validate file types
    const validExtensions = new Set(['.csv']);
    const questionsExt = questionsFile.name.toLowerCase().substring(questionsFile.name.lastIndexOf('.'));
    const optionsExt = optionsFile.name.toLowerCase().substring(optionsFile.name.lastIndexOf('.'));

    if (!validExtensions.has(questionsExt) || !validExtensions.has(optionsExt)) {
      setError('Please select CSV files');
      return;
    }

    // Validate file sizes (10MB)
    if (questionsFile.size > 10 * 1024 * 1024 || optionsFile.size > 10 * 1024 * 1024) {
      setError('CSV file size must be less than 10MB');
      return;
    }

    try {
      setUploadingCsv(true);
      setError(null);
      setShowCsvUploadModal(false); // Close upload modal

      // Analyze CSV files and show preview
      const result = await questionBanksApi.analyzeCsv(id, questionsFile, optionsFile);

      // Store files and questions for preview
      setCsvPreviewQuestions(result.questions);
      setCsvPreviewErrors(result.errors || []);
      setCsvPreviewWarnings(result.warnings || []);
      setCsvParsingMethod(result.parsing_method);
      setShowCsvPreviewModal(true);

      // Show alert if no questions extracted
      if (result.questions.length === 0 && (!result.errors || result.errors.length === 0)) {
        setAlert({ 
          message: 'No questions could be extracted from the CSV files. Please check the file format and try again.', 
          type: 'warning' 
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to analyze CSV files');
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleSaveCsvQuestions = async (questions: any[]) => {
    if (!id || isNew || savingQuestions) return; // Prevent double submission

    try {
      setSavingQuestions(true);
      setError(null);

      // Create questions from preview
      const result = await questionBanksApi.importCsv(id, questions);

      // Show success message
      const message = `Successfully created ${result.questions_created} question(s)!` +
        (result.errors && result.errors.length > 0
          ? `\n\nNote: ${result.errors.length} question(s) failed to create`
          : '');
      setAlert({ message, type: 'success' });

      // Close modal and reload questions
      setShowCsvPreviewModal(false);
      setCsvPreviewQuestions([]);
      setCsvPreviewErrors([]);
      setCsvPreviewWarnings([]);
      await loadQuestionBank();
    } catch (err: any) {
      setError(err.message || 'Failed to create questions');
    } finally {
      setSavingQuestions(false);
    }
  };

  const handleCancelCsvPreview = () => {
    setShowCsvPreviewModal(false);
    setCsvPreviewQuestions([]);
    setCsvPreviewErrors([]);
    setCsvPreviewWarnings([]);
  };

  // Helper function to check if a question is a rating scale
  const isRatingScale = (question: QuestionWithOptions): boolean => {
    return question.answer_options.some(
      opt => opt.partial_credit_weight > 0 && opt.partial_credit_weight < 1
    );
  };

  // Helper function to check if a multi-select question has partial credit
  const hasPartialCredit = (question: QuestionWithOptions): boolean => {
    return question.question_type === 'multi_select' && question.allow_partial_credit === true;
  };

  // Group questions by category
  const groupedQuestions: Record<string, QuestionWithOptions[]> = {};
  questions.forEach((question) => {
    const category = question.category || 'Uncategorized';
    if (!groupedQuestions[category]) {
      groupedQuestions[category] = [];
    }
    groupedQuestions[category].push(question);
  });

  const formatQuestionType = (type: string) => {
    return type.replaceAll('_', ' ').replaceAll(/\b\w/g, (char) => char.toUpperCase());
  };

  if (loading) {
    return <div className="loading">Loading question bank...</div>;
  }

  return (
    <div className="admin-assessment-edit">
      <div className="admin-header">
        <h1>{isNew ? 'New Question Bank' : 'Edit Question Bank'}</h1>
        <Link to="/admin/question-banks" className="btn btn-secondary">
          Back to Question Banks
        </Link>
      </div>

      {/* Only show error at top for non-alert errors (like loading errors) */}
      {error && !alert && <div className="error">{error}</div>}

      <div className="assessment-form">
        <div className="form-group">
          <label htmlFor="question-bank-name">
            Name <span className="required">*</span>
          </label>
          <input
            id="question-bank-name"
            type="text"
            value={questionBank.name || ''}
            onChange={(e) => setQuestionBank({ ...questionBank, name: e.target.value })}
            placeholder="Enter question bank name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="question-bank-description">Description</label>
          <textarea
            id="question-bank-description"
            value={questionBank.description || ''}
            onChange={(e) => setQuestionBank({ ...questionBank, description: e.target.value })}
            placeholder="Enter question bank description"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Question Bank'}
          </button>
          <Link to="/admin/question-banks" className="btn btn-secondary">
            Cancel
          </Link>
        </div>
      </div>

      {!isNew && (
        <div className="questions-section">
          <div className="questions-header">
            <div>
              <h2>Questions ({questions.length})</h2>
              <p className="form-help-text" style={{ marginTop: '0.25rem', marginBottom: 0 }}>
                Upload a PDF or CSV files to automatically extract questions, or add questions manually.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <label 
                className="btn btn-secondary" 
                style={{ 
                  cursor: (uploadingPdf || savingQuestions) ? 'not-allowed' : 'pointer', 
                  margin: 0,
                  opacity: (uploadingPdf || savingQuestions) ? 0.6 : 1
                }}
              >
                {uploadingPdf ? 'Analyzing...' : 'Upload PDF'}
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfUpload}
                  disabled={uploadingPdf || savingQuestions}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                onClick={() => setShowCsvUploadModal(true)}
                className="btn btn-secondary"
                disabled={uploadingCsv || savingQuestions || uploadingPdf}
                style={{ 
                  opacity: (uploadingCsv || savingQuestions || uploadingPdf) ? 0.6 : 1,
                  cursor: (uploadingCsv || savingQuestions || uploadingPdf) ? 'not-allowed' : 'pointer'
                }}
              >
                Import CSV
              </button>
              <button
                onClick={() => {
                  setEditingQuestion(null);
                  setShowQuestionForm(true);
                }}
                className="btn btn-primary"
              >
                Add Question
              </button>
            </div>
          </div>
          <div id="question-editor-anchor">
            {showQuestionForm && (
              <QuestionEditor
                question={editingQuestion || undefined}
                onSave={editingQuestion ? handleUpdateQuestion : handleSaveQuestion}
                onCancel={handleCancelQuestion}
              />
            )}
          </div>

          {Object.keys(groupedQuestions).length === 0 ? (
            <div className="empty-state">No questions yet. Add your first question above.</div>
          ) : (
            Object.keys(groupedQuestions)
              .sort((a, b) => a.localeCompare(b))
              .map((category) => (
                <div key={category} className="question-category-group">
                  <h3 className="category-header">{category}</h3>
                  <div className="questions-list">
                    {groupedQuestions[category].map((question, index) => (
                      <div key={question.id} className="question-card">
                        <div className="question-header">
                          <span className="question-number">Question {index + 1}</span>
                          <span className="question-type">
                            {formatQuestionType(question.question_type)}
                          </span>
                          {isRatingScale(question) && (
                            <span 
                              style={{ 
                                marginLeft: '0.5rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#e8f4f8',
                                color: '#3498db',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                border: '1px solid #3498db'
                              }}
                              title="This is a rating scale question. Points are awarded based on the selected option's weight."
                            >
                              📊 Rating Scale
                            </span>
                          )}
                          {hasPartialCredit(question) && (
                            <span 
                              style={{ 
                                marginLeft: '0.5rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#fff4e6',
                                color: '#f39c12',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                border: '1px solid #f39c12'
                              }}
                              title="This multi-select question uses partial credit. Points are awarded proportionally based on the number of correct selections."
                            >
                              ⚖️ Partial Credit
                            </span>
                          )}
                          <span className="question-points">{question.points_possible} points</span>
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="btn btn-small btn-secondary"
                            style={{ marginLeft: 'auto' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="btn btn-small btn-danger"
                          >
                            Delete
                          </button>
                        </div>
                        <div className="question-content">
                          <p>{question.prompt_text}</p>
                          {question.answer_options.length > 0 && (
                            <div className="answer-options">
                              <ul>
                                {question.answer_options.map((option) => (
                                  <li key={option.id}>
                                    {option.option_text}
                                    {option.is_correct && (
                                      <span className="correct-badge">Correct</span>
                                    )}
                                    {option.partial_credit_weight > 0 &&
                                      option.partial_credit_weight < 1 && (
                                        <span className="correct-badge">
                                          {Math.round(option.partial_credit_weight * 100)}% credit
                                        </span>
                                      )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {showPreviewModal && (
        <PDFPreviewModal
          questions={previewQuestions}
          onSave={handleSavePreviewQuestions}
          onCancel={handleCancelPreview}
          onReanalyze={handleReanalyzePdf}
          isReanalyzing={isReanalyzing}
          isSaving={savingQuestions}
          errors={previewErrors}
        />
      )}

      {showCsvUploadModal && (
        <CSVUploadModal
          isOpen={showCsvUploadModal}
          onClose={() => setShowCsvUploadModal(false)}
          onUpload={handleCsvUpload}
          disabled={uploadingCsv || savingQuestions}
          isUploading={uploadingCsv}
        />
      )}

      {showCsvPreviewModal && (
        <CSVPreviewModal
          questions={csvPreviewQuestions}
          onSave={handleSaveCsvQuestions}
          onCancel={handleCancelCsvPreview}
          isSaving={savingQuestions}
          errors={csvPreviewErrors}
          warnings={csvPreviewWarnings}
          parsingMethod={csvParsingMethod}
        />
      )}

      {/* Loading modals */}
      {uploadingPdf && (
        <LoadingModal
          message="Analyzing PDF..."
          subMessage="This may take a minute. Please wait while we extract questions from your document."
        />
      )}

      {isReanalyzing && (
        <LoadingModal
          message="Re-analyzing PDF..."
          subMessage="Processing your additional context and re-extracting questions. This may take a moment."
        />
      )}

      {uploadingCsv && (
        <LoadingModal
          message="Analyzing CSV files..."
          subMessage="This may take a minute. Please wait while we parse and extract questions from your CSV files."
        />
      )}

      {savingQuestions && (
        <LoadingModal
          message="Saving Questions..."
          subMessage="Creating questions in the question bank. Please do not close this page."
        />
      )}

      <AlertModal
        isOpen={!!alert}
        onClose={() => setAlert(null)}
        message={alert?.message || ''}
        type={alert?.type || 'info'}
      />
    </div>
  );
};
